/*! Copyright (c) Safe As Milk. All rights reserved. */
import { debounce } from "utils";

class StickyScroll extends HTMLElement {
    #ticking;
    #topGap;
    #maxDisplacement;
    #scrollPosition;
    #intersectionObserver;
    #topPosition;
    #mediaQuery;
    #boundViewportChange;
    #cleanup;
    #animationFrame;
    constructor() {
        super();
        this.#topGap = 18;
        this.#intersectionObserver = null;
        this.#mediaQuery = window.matchMedia("(min-width: 61.25em)");
        this.#boundViewportChange = this.#syncViewport.bind(this);
    }
    connectedCallback() {
        this.style.display = "block";
        this.#mediaQuery.addEventListener("change", this.#boundViewportChange);
        this.#syncViewport();
    }
    disconnectedCallback() {
        this.#mediaQuery.removeEventListener("change", this.#boundViewportChange);
        this.#deactivate();
    }
    #syncViewport() {
        if (this.#mediaQuery.matches) {
            if (!this.#cleanup) this.#activate();
        } else {
            this.#deactivate();
        }
    }
    #deactivate() {
        this.#cleanup?.();
        this.#cleanup = null;
        cancelAnimationFrame(this.#animationFrame);
        this.#ticking = false;
        this.#topPosition = null;
        this.style.removeProperty("position");
        this.style.removeProperty("top");
        this.classList.remove("is-scrollable", "is-not-scrollable");
    }
    #activate() {
        let active = true;
        const scrollListener = () => {
            if (!this.#ticking) {
                this.#animationFrame = window.requestAnimationFrame((() => {
                    if (active) this.#setStickyDisplacement();
                    this.#ticking = false;
                }));
                this.#ticking = true;
            }
        };
        const handleIntersection = entries => {
            if (!active) return;
            entries.forEach((entry => {
                if (entry.isIntersecting) {
                    window.addEventListener("scroll", scrollListener);
                } else {
                    window.removeEventListener("scroll", scrollListener);
                }
            }));
        };
        const updateMaxDisplacement = () => {
            const getHeightWithBottomMargin = () => {
                const paddingBottom = Number(window.getComputedStyle(this).getPropertyValue("padding-bottom").replace("px", ""));
                if (!paddingBottom) {
                    this.style.paddingBottom = "1px";
                    const fullHeight = this.offsetHeight - 1;
                    this.style.paddingBottom = null;
                    return fullHeight;
                } else {
                    return this.offsetHeight;
                }
            };
            this.#maxDisplacement = window.innerHeight - getHeightWithBottomMargin() - 18;
        };
        const updateObserver = () => {
            if (!active || !this.isConnected || !this.#mediaQuery.matches) return;
            this.#setTopGap();
            if (!this.#topPosition) this.#topPosition = this.#topGap;
            updateMaxDisplacement();
            this.#scrollPosition = window.scrollY;
            this.#setStyles();
            if (window.innerHeight < this.offsetHeight) {
                if (!this.#intersectionObserver) {
                    this.#intersectionObserver = new IntersectionObserver(handleIntersection.bind(this), {
                        rootMargin: "0px"
                    });
                }
                this.#intersectionObserver.observe(this);
                this.classList.add("is-scrollable");
                this.classList.remove("is-not-scrollable");
            } else if (window.innerHeight >= this.offsetHeight && this.#intersectionObserver) {
                this.#intersectionObserver.disconnect();
                window.removeEventListener("scroll", scrollListener);
                this.classList.add("is-not-scrollable");
                this.classList.remove("is-scrollable");
            }
        };
        const scheduleUpdate = debounce(updateObserver, 100);
        const resizeObserver = new ResizeObserver(scheduleUpdate);
        this.#cleanup = () => {
            active = false;
            window.removeEventListener("resize", scheduleUpdate);
            window.removeEventListener("scroll", scrollListener);
            resizeObserver.disconnect();
            this.#intersectionObserver?.disconnect();
            this.#intersectionObserver = null;
        };
        updateObserver();
        window.addEventListener("resize", scheduleUpdate, { passive: true });
        resizeObserver.observe(this);
    }
    #setTopGap() {
        const header = document.querySelector("header");
        this.#topGap = header.getAttribute("data-sticky-header") === "true" ? header.offsetHeight + 18 : 18;
    }
    #setStyles() {
        this.style.display = "block";
        this.style.position = "sticky";
        this.style.top = `${this.#topPosition}px`;
        this.classList.add("is-not-scrollable");
    }
    #setStickyDisplacement() {
        const stickyElementTop = Number(this.style.top.replace("px", ""));
        if (window.scrollY < this.#scrollPosition) {
            if (stickyElementTop < this.#topGap) {
                const topMath = stickyElementTop + this.#scrollPosition - window.scrollY;
                this.style.top = `${topMath}px`;
                this.#topPosition = topMath;
                if (topMath >= this.#topGap) {
                    this.style.top = `${this.#topGap}px`;
                    this.#topPosition = this.#topGap;
                }
            } else if (stickyElementTop >= this.#topGap) {
                this.style.top = `${this.#topGap}px`;
                this.#topPosition = this.#topGap;
            }
        } else if (stickyElementTop > this.#maxDisplacement) {
            const topMath = stickyElementTop + this.#scrollPosition - window.scrollY;
            this.style.top = `${topMath}px`;
            this.#topPosition = topMath;
            if (topMath <= this.#maxDisplacement) {
                this.style.top = `${this.#maxDisplacement}px`;
                this.#topPosition = this.#maxDisplacement;
            }
        } else if (stickyElementTop <= this.#maxDisplacement) {
            this.style.top = `${this.#maxDisplacement}px`;
            this.#topPosition = this.#maxDisplacement;
        }
        this.#scrollPosition = window.scrollY;
    }
}

customElements.define("sticky-scroll", StickyScroll);
//# sourceMappingURL=sticky-scroll.js.map
